import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import { listOfficialVaultRequests } from 'src/pages/admin/services/officialVaultService';
import { Vault, listVaults } from 'src/pages/admin/services/vaultService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { type ApplicationEnding, isSettling, listErrorMessage, managedVaults } from './helpers';
import { VAULTS_NS } from './constants';

/** How often the list re-reads itself while a vault is settling (XBIT-4439; XBIT-4383's interval). */
export const POLL_INTERVAL_MS = 10_000;

/**
 * How long one settling vault keeps the list polling, from when this page first saw it settle (XBIT-4439). A stake
 * that never subscribes (refused) leaves its vault ACTIVE with 0 depositors, and must not poll for as long as the page
 * stays open; Refresh still reads.
 */
export const SETTLING_POLL_LIMIT_MS = 5 * 60_000;

/**
 * `GET /vault/vaults`, in the engine's order (official first), without DRAFT vaults or the closed drafts of rejected and
 * cancelled applications (see managedVaults; XBIT-4699 reads GET /vault/official-vaults beside it). If that second read
 * fails the list is still shown, from the last applications read (none on the first), with `applicationsNotice` (a
 * warning for the vaults list only, never `loadError`). While a vault
 * is settling (isSettling) and the tab is visible, it re-reads itself every POLL_INTERVAL_MS, silently: no loading
 * flag, and a failed poll keeps the list it has. A read lands only if no newer read has landed first, so a slow poll
 * never undoes a refetch (the pattern of XBIT-4383's useOfficialVaultRequests).
 */
/** With `enabled` false (the role cannot read vaults here, e.g. the approvals-only Operations page) nothing is read. */
export const useVaults = (enabled = true) => {
  const translate = useTranslate();
  // react-admin's translate is not identity-stable; a ref keeps refetch stable (see useVaultParams).
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const [vaults, setVaults] = useState<Vault[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Not an error of the vaults read: Records and Investors treat any loadError as "the vaults failed to load" and stop.
  // This one only says the list is unfiltered (XBIT-4699), and only the vaults list shows it.
  const [applicationsNotice, setApplicationsNotice] = useState<string | null>(null);

  // Reads are numbered as they start; `landed` is the newest one applied.
  const started = useRef(0);
  const landed = useRef(0);
  // When this page first saw each settling vault settle; one past SETTLING_POLL_LIMIT_MS stops counting.
  const settlingSince = useRef(new Map<string, number>());

  // The applications the last successful read returned; a failed read falls back to it rather than un-hiding drafts.
  const applications = useRef<ApplicationEnding[]>([]);

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const load = useCallback(async (silent: boolean) => {
    if (!enabledRef.current) return;
    started.current += 1;
    const read = started.current;
    if (!silent) setLoading(true);
    try {
      const [vaultList, applicationList] = await Promise.all([
        listVaults(),
        listOfficialVaultRequests().catch(() => null),
      ]);
      if (read < landed.current) return;
      landed.current = read;
      if (applicationList) applications.current = applicationList;
      const list = managedVaults(vaultList, applications.current);
      const now = Date.now();
      const since = new Map<string, number>();
      list
        .filter(isSettling)
        .forEach((v) => since.set(v.vaultId, settlingSince.current.get(v.vaultId) ?? now));
      settlingSince.current = since;
      setVaults(list);
      setLoadError(null);
      // Fail open (XBIT-4699): the list is shown filtered with the last good applications read, and the notice says so.
      // A good read clears it; a silent poll that fails the same way leaves it.
      if (applicationList) setApplicationsNotice(null);
      else if (!silent)
        setApplicationsNotice(translateRef.current(`${VAULTS_NS}.applicationsUnavailable`));
    } catch (error) {
      if (!silent && read > landed.current)
        setLoadError(listErrorMessage(parseApiError(error), translateRef.current));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => load(false), [load]);

  useEffect(() => {
    refetch();
  }, [refetch, enabled]);

  const polling = vaults.some(isSettling);
  useEffect(() => {
    if (!polling) return undefined;
    // The limit is read when the tick fires, not at render: a failed silent poll sets no state, so a limit decided at
    // render would never be re-read while the BFF is down (ea's review m1).
    const timer = setInterval(() => {
      const now = Date.now();
      const live = Array.from(settlingSince.current.values()).some(
        (since) => now - since < SETTLING_POLL_LIMIT_MS
      );
      if (live && document.visibilityState === 'visible') load(true);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [polling, load]);

  return { vaults, loading, loadError, applicationsNotice, refetch };
};
